import React, { useEffect, useRef, useState } from 'react';
import { AutoComplete, Spin } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { searchLocations, suggestLocations } from '../services/LocationService';
import { ILocation } from '../interfaces/heavyDutyMachinery/ILocation';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import FloatingLabelInput from './FloatingLabelInput';

// Azure Maps is billed per search transaction, so every keystroke firing a
// request adds up fast — wait for a pause in typing (debounce) and require
// a couple of characters before spending a call on it at all. Exported so
// other from-scratch location search UIs (e.g. the mobile search bar's own
// full-screen "Where" picker, which can't reuse this component's own
// AutoComplete-popup-based rendering) debounce identically.
export const LOCATION_SEARCH_DEBOUNCE_MS = 500;
export const LOCATION_SEARCH_MIN_QUERY_LENGTH = 2;

// Azure Maps' own entityType for a geography-level match — the same generic
// hierarchy for every country, so this needs no per-country logic. Shown
// alongside the suggestion so e.g. "Burgos" the city and "Burgos" the
// province — two separate, easily-confused results — are told apart.
const ADMINISTRATIVE_LEVEL_LABEL_KEYS: Record<string, string> = {
  Country: TRANSLATIONS.LOCATION_LEVEL_COUNTRY,
  CountrySubdivision: TRANSLATIONS.LOCATION_LEVEL_REGION,
  CountrySecondarySubdivision: TRANSLATIONS.LOCATION_LEVEL_PROVINCE,
  Municipality: TRANSLATIONS.LOCATION_LEVEL_CITY_TOWN,
  MunicipalitySubdivision: TRANSLATIONS.LOCATION_LEVEL_DISTRICT,
  Neighbourhood: TRANSLATIONS.LOCATION_LEVEL_NEIGHBOURHOOD,
  PostalCodeArea: TRANSLATIONS.LOCATION_LEVEL_POSTAL_CODE_AREA,
};

/**
 * Builds the same "full address (+ admin level) + optional country" label
 * from a raw ILocation that this component's own dropdown rows use --
 * exported so a from-scratch UI (not an AutoComplete popup) can render
 * identical suggestion text without duplicating this logic.
 */
export const formatLocationLabel = (location: ILocation, t: TFunction, includeCountry?: boolean): string => {
  const baseLabel = location.formattedAddress || location.locality;
  const levelLabelKey = ADMINISTRATIVE_LEVEL_LABEL_KEYS[location.administrativeLevel];
  const levelLabel = levelLabelKey ? translate(t, levelLabelKey) : undefined;
  const withLevel = levelLabel ? `${baseLabel} (${levelLabel})` : baseLabel;
  const alreadyHasCountry = !!location.country && baseLabel.toLowerCase().trim().endsWith(location.country.toLowerCase());
  return includeCountry && location.country && !alreadyHasCountry ? `${withLevel}, ${location.country}` : withLevel;
};

interface LocationOption extends DefaultOptionType {
  value: string;
  label: React.ReactNode;
  location: ILocation;
}

export interface LocationAutocompleteProps {
  value?: string;
  onChange?: (city: string) => void;
  /**
   * Fired with the full geocoded result when the user picks a suggestion —
   * use it to also store province/latitude/longitude (this component only
   * carries the city string as its Form.Item value, like a plain text input).
   */
  onSelectLocation?: (location: ILocation) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  style?: React.CSSProperties;
  /**
   * Appends ", {country}" to each suggestion's label — for forms that no
   * longer have a separate country picker and rely on the address itself
   * to convey the country (see onSelectLocation for reading the country
   * value: still comes back on the same location.country field either way).
   */
  includeCountryInLabel?: boolean;
  /**
   * Searches this app's own stored locations (HeavyDutyMachineryLocation) instead of
   * Azure Maps -- use on the Wall's browse-page filter, so a suggestion is
   * never a place with zero matching spaces (and two spaces sharing the
   * same location only ever surface as one option, per HeavyDutyMachineryLocation's
   * unique DedupeKey). Leave unset on the publish/edit form, which needs
   * Azure Maps to geocode brand-new places with no prior data.
   */
  useOwnData?: boolean;
  /**
   * Only commits the value (calls onChange) when the user picks a
   * suggestion or clears the input -- never on every keystroke. Use this
   * on filters, where a value change re-triggers a list fetch: without
   * it, typing "Val" fires a search for city="Val" on every character,
   * before the user has picked a real place.
   */
  commitOnSelectOnly?: boolean;
  /** Passed straight through to the underlying AutoComplete -- 'borderless' lets this
   * blend into a container that already draws its own border (e.g. the pill search bar). */
  variant?: 'outlined' | 'borderless' | 'filled' | 'underlined';
  /**
   * Renders with the app's creamy pill/floating-label chrome instead of a
   * plain antd input -- same customizeInput trick as
   * FloatingLabelAutoComplete (passing FloatingLabelInput as AutoComplete's
   * child), just applied here too since Machinery's own address field never
   * got it (plain antd look) and a caller asking for the pill treatment
   * shouldn't have to build a second component to get it. Omit to keep the
   * existing plain look untouched -- every current caller (Machinery's
   * create/edit forms) leaves this unset, so their rendering is unaffected.
   */
  floatingLabel?: string;
}

/**
 * Ported from Authentication76's LocationAutocomplete (see that copy's own
 * comment) — same Azure Maps-backed search (HeavyDutyMachineryApi's
 * LocationsController), plus the useOwnData mode backed by this app's own
 * HeavyDutyMachineryLocation cache table.
 *
 * Behaves like a normal Form.Item-compatible text input; the richer geocode
 * data (province, coordinates) is surfaced separately via onSelectLocation
 * since a Form.Item only tracks one value per field.
 *
 * What's TYPED IN THE BOX and what's SUBMITTED as the "city" value are
 * deliberately different: after picking a suggestion, the box shows the
 * full address ("Barrasa, Valle de Mena, Spain") so the user can see what
 * they actually picked, but the Form value stays the plain locality
 * ("Valle de Mena").
 */
const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  onSelectLocation,
  placeholder,
  disabled,
  allowClear,
  style,
  includeCountryInLabel,
  useOwnData,
  commitOnSelectOnly,
  variant,
  floatingLabel,
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  // Once the user has typed or picked something, the box's text is fully
  // ours to manage — stop mirroring the (now-diverged) external value prop.
  const interactedRef = useRef(false);

  useEffect(() => {
    if (!interactedRef.current) {
      setInputValue(value ?? '');
    }
  }, [value]);

  const handleSearch = (query: string) => {
    clearTimeout(debounceRef.current);
    if (query.trim().length < LOCATION_SEARCH_MIN_QUERY_LENGTH) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = useOwnData ? await suggestLocations(query) : await searchLocations(query);
        setOptions(
          results.map((location, index) => {
            // The dropdown ROW shows the full address (street/postal code
            // included) so suggestions that share the same locality/province
            // are still distinguishable — but the option's value (what
            // actually gets submitted as the "city" field) stays the plain
            // locality.
            const label = formatLocationLabel(location, t, includeCountryInLabel);
            // value must be unique per row, not just the key: antd/rc-select
            // resolves a click through an internal value-keyed map, so two
            // options sharing the same value (e.g. "Burgos" exists in both
            // Spain and the Philippines) collapse to whichever one wins that
            // map — not necessarily the one actually clicked. The real
            // locality is read off option.location in handleSelect below,
            // never off this synthetic id.
            const id = `${location.locality}__${index}`;
            return { key: id, value: id, label, location };
          })
        );
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, LOCATION_SEARCH_DEBOUNCE_MS);
  };

  const handleChange = (v: string) => {
    interactedRef.current = true;
    setInputValue(v);
    // rc-select's AutoComplete fires onChange with the *selected option's
    // own value* the moment a suggestion is picked -- in addition to (not
    // instead of) firing onSelect separately, which is where handleSelect
    // below commits the real, clean locality. That value here is one of
    // handleSearch's synthetic "locality__index" ids, never real typed
    // text, and forwarding it straight to the Form clobbered whatever
    // handleSelect had just set -- for a mouse click exactly as much as a
    // keyboard Enter (both fire the same onChange). Bail out on it so
    // handleSelect is the only path that ever commits a value on selection.
    if (options.some((o) => o.value === v)) return;
    // With commitOnSelectOnly, free typing only updates the box's own text
    // (above) -- the external value (and whatever fetch depends on it) is
    // committed by handleSelect below, or here when cleared.
    if (!commitOnSelectOnly || !v) {
      onChange?.(v);
    }
  };

  const handleSelect = (_selectedValue: string, option: LocationOption) => {
    interactedRef.current = true;
    // Show the full address the user picked, not just the locality that
    // was just submitted to the Form via handleChange above. Every
    // option's label is always built as a plain string in handleSearch
    // below.
    setInputValue(option.label as string);
    // option.value is the synthetic per-row id from handleSearch above —
    // always commit the real place name, read off option.location, never
    // that id. Falls back to province/region since a geography-level pick
    // above city level (e.g. selecting the province or state itself) has
    // no locality.
    const { locality, province, region } = option.location;
    onChange?.(locality || province || region);
    onSelectLocation?.(option.location);
  };

  return (
    <AutoComplete
      value={inputValue}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      onChange={handleChange}
      // FloatingLabelInput shows its own label in place of a placeholder --
      // a separate placeholder string on the AutoComplete itself would be
      // redundant (and never shows anyway once customizeInput takes over).
      placeholder={floatingLabel ? undefined : placeholder}
      disabled={disabled}
      allowClear={allowClear}
      variant={variant}
      style={style}
      notFoundContent={loading ? <Spin size="small" /> : null}
      filterOption={false}
      // Let the popup grow to fit the longest suggestion instead of being
      // clipped to the (often much narrower) input's width -- capped back
      // down to the viewport by .heavyDutyMachinery-location-popup below once a
      // suggestion (full address + admin level + country) is long enough
      // that "grow freely" means "wider than the phone screen itself".
      popupMatchSelectWidth={false}
      popupClassName="heavyDutyMachinery-location-popup"
      // Reported on-device: this field's own suggestions sometimes got
      // replaced by Safari's NATIVE address-autofill dropdown instead --
      // system font, edge-to-edge, dashed underlines under the matched
      // text, no rounded card. rc-select already defaults autoComplete to
      // "off", but Safari's address-autofill heuristic is known to ignore
      // that literal value on fields it decides look address-like (this
      // form also has a privateAddress field, which likely tips it over).
      // A nonstandard token isn't a recognized autofill category at all,
      // so there's nothing for that heuristic to match. Spread rather than
      // a plain attribute: antd's own AutoCompleteProps type doesn't
      // declare autoComplete even though rc-select's Input does forward it
      // (confirmed in rc-select/es/Selector/Input.js) -- a real gap in
      // antd's public types, not a sign this prop doesn't work.
      {...{ autoComplete: 'heavyDutyMachinery-location-search' }}
    >
      {floatingLabel ? <FloatingLabelInput label={floatingLabel} disabled={disabled} /> : undefined}
    </AutoComplete>
  );
};

export default LocationAutocomplete;
